// src/components/Chat/ChatInterface.tsx
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { Order, Message, MessageReaction, OrderStatus } from '../../types';
import OrderCard from './OrderCard';
import DepositPage from './DepositPage';
import AdminDepositPage from './AdminDepositPage';
import AdminOrderPricingPage from './AdminOrderPricingPage';
import AdminUserStatisticsPage from './AdminUserStatisticsPage';
import AdminMaintenancePage from './AdminMaintenancePage';
import ArticlesPage from './ArticlesPage';
import OrderManagementPage from './OrderManagementPage';
import { estimateBulkOrderPrice, OrderPricingOption, parseBulkOrderInput } from './orderPricing';
import { apiService } from '../../services/api.service';

interface ChatInterfaceProps {
    userId: string;
    username: string;
}

interface DepositHistoryItem {
    transactionId: string;
    amount: number;
    status: string;
    createdAt?: string;
}

const ORDER_SIDEBAR_PAGE_SIZE = 20;
const CHAT_HISTORY_PAGE_SIZE = 10;
const MESSAGE_REACTIONS = [
    { emoji: '❤️', label: 'Tim' },
    { emoji: '😂', label: 'Haha' },
    { emoji: '😢', label: 'Buồn' },
    { emoji: '😮', label: 'Wow' },
    { emoji: '😡', label: 'Phẫn nộ' },
    { emoji: '👍', label: 'Like' },
];

const ChatInterface: React.FC<ChatInterfaceProps> = ({ userId, username }) => {
    const { socketService, isConnected } = useSocket();
    const { user, logout } = useAuth();
    const currentUserId = user?._id || userId || (() => {
        try {
            const savedUser = localStorage.getItem('user_data');
            return savedUser && savedUser !== 'undefined' && savedUser !== 'null' ? JSON.parse(savedUser)._id : '';
        } catch {
            return '';
        }
    })();
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
    const [showScrollToLatest, setShowScrollToLatest] = useState(false);
    const [inputMessage, setInputMessage] = useState('');
    const [replyingTo, setReplyingTo] = useState<Message | null>(null);
    const [markingUnreadId, setMarkingUnreadId] = useState<string | null>(null);
    const [messageActionError, setMessageActionError] = useState('');
    const [activeReactionMessageId, setActiveReactionMessageId] = useState<string | null>(null);
    const [reactingMessageId, setReactingMessageId] = useState<string | null>(null);
    const [approvingRedoMessageId, setApprovingRedoMessageId] = useState<string | null>(null);
    const [approvedRedoMessageIds, setApprovedRedoMessageIds] = useState<string[]>([]);
    const [approvingRefundMessageId, setApprovingRefundMessageId] = useState<string | null>(null);
    const [approvedRefundMessageIds, setApprovedRefundMessageIds] = useState<string[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [isOrderCodeCopied, setIsOrderCodeCopied] = useState(false);
    const [isLoadingOrderPage, setIsLoadingOrderPage] = useState(false);
    const [hasMoreOrders, setHasMoreOrders] = useState(true);
    const [ordersLoadError, setOrdersLoadError] = useState('');
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [successfulOrderCount, setSuccessfulOrderCount] = useState<number | null>(null);
    const [onlineUserCount, setOnlineUserCount] = useState<number | null>(null);
    const [orderCountError, setOrderCountError] = useState('');
    const [orderCountRefreshToken, setOrderCountRefreshToken] = useState(0);
    const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
    const [updatingPriorityOrderId, setUpdatingPriorityOrderId] = useState<string | null>(null);
    const userRoomId = `user_${String(currentUserId || user?._id || '')}`;
    const [conversations, setConversations] = useState<Array<{
        roomId: string;
        participantId?: string;
        participantName?: string;
        participantUsername?: string;
        participantRole?: string;
        lastMessage?: string;
        lastSenderUsername?: string;
        unreadCount?: number;
    }>>([]);
    const [activeRoomId, setActiveRoomId] = useState<string>('');
    const [activeNav, setActiveNav] = useState<'home' | 'orders' | 'articles' | 'recharge' | 'admin-recharge' | 'admin-pricing' | 'admin-statistics' | 'admin-maintenance'>('orders');
    const [maintenanceEnabled, setMaintenanceEnabled] = useState<boolean | null>(null);
    const [maintenanceStatusError, setMaintenanceStatusError] = useState('');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isDarkTheme, setIsDarkTheme] = useState(
        () => window.localStorage.getItem('chat-theme') !== 'light'
    );
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [passwordError, setPasswordError] = useState('');
    const [passwordMessage, setPasswordMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [rechargeHistory, setRechargeHistory] = useState<DepositHistoryItem[]>([]);
    const [isRechargeHistoryLoading, setIsRechargeHistoryLoading] = useState(false);
    const [orderCode, setOrderCode] = useState('');
    const [orderSteps, setOrderSteps] = useState(1);
    const [showBulkOrderHelp, setShowBulkOrderHelp] = useState(false);
    const [orderEntryError, setOrderEntryError] = useState('');
    const [orderEntryMessage, setOrderEntryMessage] = useState('');
    const [orderPricing, setOrderPricing] = useState<OrderPricingOption[]>([]);
    const hasInitializedOrderPricingRef = useRef(false);
    const selectedOrderPricing = orderPricing.find((item) => item.totalSteps === orderSteps);
    const orderPriceEstimate = estimateBulkOrderPrice(orderCode, orderSteps, orderPricing);
    const [partnerOutstanding, setPartnerOutstanding] = useState({ unpaidTotal: 0, outstandingSteps: 0 });
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messageListRef = useRef<HTMLDivElement>(null);
    const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const historyCursorRef = useRef<string | undefined>(undefined);
    const hasMoreHistoryRef = useRef(false);
    const isLoadingOlderMessagesRef = useRef(false);
    const pendingHistoryBeforeRef = useRef<string | null>(null);
    const pendingScrollRestoreRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
    const scrollToLatestRef = useRef(false);
    const orderListRef = useRef<HTMLDivElement>(null);
    const orderOffsetRef = useRef(0);
    const orderPageLoadingRef = useRef(false);
    const hasMoreOrdersRef = useRef(true);
    const retryOrderPageFromStartRef = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const accountMenuRef = useRef<HTMLDivElement>(null);
    const bulkOrderHelpButtonRef = useRef<HTMLButtonElement>(null);
    const bulkOrderHelpPopupRef = useRef<HTMLDivElement>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const partnerSummaryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const displayName = user?.fullName || user?.username || 'Người dùng';

    useEffect(() => {
        window.localStorage.setItem('chat-theme', isDarkTheme ? 'dark' : 'light');
    }, [isDarkTheme]);

    const refreshMaintenanceStatus = useCallback(async () => {
        try {
            const status = await apiService.get<{ enabled: boolean }>('/system-settings/maintenance');
            setMaintenanceEnabled(status.enabled);
            setMaintenanceStatusError('');
        } catch (error) {
            console.error('Không tải được trạng thái bảo trì:', error);
            setMaintenanceEnabled(null);
            setMaintenanceStatusError('Không tải được trạng thái bảo trì. Tạm thời không thể tạo mã chung sức hoặc nạp tiền.');
        }
    }, []);

    useEffect(() => {
        void refreshMaintenanceStatus();
    }, [refreshMaintenanceStatus]);

    const refreshPartnerOutstanding = useCallback(() => {
        if (user?.role !== 'partner') return;
        if (partnerSummaryTimerRef.current) clearTimeout(partnerSummaryTimerRef.current);
        partnerSummaryTimerRef.current = setTimeout(() => {
            void apiService.get<{ unpaidTotal: number; outstandingSteps: number }>('/orders/summary')
                .then(setPartnerOutstanding)
                .catch((error) => console.error('Không tải được tổng công nợ mã chung sức:', error));
        }, 300);
    }, [user?.role]);

    const loadOrderPage = useCallback(async (reset = false) => {
        if (orderPageLoadingRef.current || (!reset && !hasMoreOrdersRef.current)) return;

        orderPageLoadingRef.current = true;
        setIsLoadingOrderPage(true);
        setOrdersLoadError('');
        const offset = reset ? 0 : orderOffsetRef.current;
        retryOrderPageFromStartRef.current = reset;

        try {
            const response = await apiService.get<{ total: number; data: Order[] }>(
                `/orders?limit=${ORDER_SIDEBAR_PAGE_SIZE}&offset=${offset}`,
            );
            const receivedOrders = response.data || [];
            orderOffsetRef.current = offset + receivedOrders.length;
            const hasMore = orderOffsetRef.current < response.total;
            hasMoreOrdersRef.current = hasMore;
            setHasMoreOrders(hasMore);
            setOrders((currentOrders) => {
                if (reset) return receivedOrders;
                const existingIds = new Set(currentOrders.map((order) => order._id));
                return [...currentOrders, ...receivedOrders.filter((order) => !existingIds.has(order._id))];
            });
        } catch (error) {
            console.error('Không tải được danh sách mã chung sức:', error);
            setOrdersLoadError('Không tải được danh sách mã chung sức.');
        } finally {
            orderPageLoadingRef.current = false;
            setIsLoadingOrderPage(false);
        }
    }, []);

    useEffect(() => {
        if (activeNav === 'home' && user?.role !== 'admin') {
            void loadOrderPage(true);
        }
    }, [activeNav, user?.role, loadOrderPage]);

    useEffect(() => {
        if (activeNav !== 'orders') return;

        let isCurrent = true;
        apiService.get<{ total: number; successfulTotal: number }>('/orders/unfinished-count')
            .then((response) => {
                if (!isCurrent) return;
                setSuccessfulOrderCount(response.successfulTotal);
                setOrderCountError('');
            })
            .catch((error) => {
                if (!isCurrent) return;
                console.error('Không tải được số mã chung sức đã thành công:', error);
                setOrderCountError('Không tải được số mã chung sức đã thành công.');
            });

        return () => {
            isCurrent = false;
        };
    }, [activeNav, orderCountRefreshToken]);

    useEffect(() => {
        const handleOnlineUserCount = (data: { count: number }) => {
            if (Number.isInteger(data?.count) && data.count >= 0) {
                setOnlineUserCount(data.count);
            }
        };

        socketService.on('visitors:count', handleOnlineUserCount);
        if (isConnected) {
            socketService.emit('visitors:count:get');
        }

        return () => socketService.off('visitors:count', handleOnlineUserCount);
    }, [isConnected, socketService]);

    const handleOrderListScroll = () => {
        const list = orderListRef.current;
        if (list && list.scrollHeight - list.scrollTop - list.clientHeight < 120) {
            void loadOrderPage();
        }
    };

    useEffect(() => {
        refreshPartnerOutstanding();
        return () => {
            if (partnerSummaryTimerRef.current) clearTimeout(partnerSummaryTimerRef.current);
        };
    }, [orders, refreshPartnerOutstanding]);

    const initials = (displayName || 'N')
        .split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const loadRechargeHistory = async () => {
        setIsRechargeHistoryLoading(true);
        try {
            const response = await apiService.get<{ data: DepositHistoryItem[] }>('/transactions/history?limit=3&offset=0');
            setRechargeHistory(response.data || []);
        } finally {
            setIsRechargeHistoryLoading(false);
        }
    };

    useEffect(() => {
        apiService.get<OrderPricingOption[]>('/orders/pricing')
            .then((pricing) => {
                const options = pricing || [];
                setOrderPricing(options);
                if (!hasInitializedOrderPricingRef.current && options.length > 0) {
                    setOrderSteps(options[0].totalSteps);
                    hasInitializedOrderPricingRef.current = true;
                }
            })
            .catch(() => setOrderPricing([]));
    }, [activeNav, user?._id, user?.role]);

    useEffect(() => {
        if (!showBulkOrderHelp) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (bulkOrderHelpButtonRef.current?.contains(target) || bulkOrderHelpPopupRef.current?.contains(target)) return;
            setShowBulkOrderHelp(false);
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [showBulkOrderHelp]);

    const formatRechargeDate = (value?: string) => value
        ? new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value))
        : '-';

    const getRechargeStatusLabel = (status: string) => ({
        success: 'Thành công',
        pending: 'Đang xử lý',
        pending_admin: 'Chờ admin duyệt',
        processing: 'Đang xử lý',
        expired: 'Hết hạn',
        failed: 'Thất bại',
        cancelled: 'Đã hủy',
    }[status] || status);

    const getRoleLabel = (role?: string) => ({
        admin: 'Quản trị viên',
        partner: 'Đối tác',
        user: 'Thành viên',
    }[role || ''] || role || 'Thành viên');

    const getOrderStatusLabel = (status?: string) => ({
        PENDING: 'Đang chờ',
        CODE_RECEIVED: 'Đã nhận mã',
        PROCESSING: 'Đang xử lý',
        COMPLETED: 'Hoàn thành',
        ERROR: 'Lỗi',
        CANCELED: 'Đã hủy',
    }[status || ''] || status || 'Không xác định');

    const formatMessageTime = (timestamp: Date) =>
        new Intl.DateTimeFormat('vi-VN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(new Date(timestamp));

    const isSameCalendarDay = (first: Date, second: Date) => {
        const firstDate = new Date(first);
        const secondDate = new Date(second);

        return firstDate.getFullYear() === secondDate.getFullYear()
            && firstDate.getMonth() === secondDate.getMonth()
            && firstDate.getDate() === secondDate.getDate();
    };

    const formatMessageDay = (timestamp: Date) => {
        const messageDate = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        if (isSameCalendarDay(messageDate, today)) return 'Hôm nay';
        if (isSameCalendarDay(messageDate, yesterday)) return 'Hôm qua';

        return new Intl.DateTimeFormat('vi-VN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(messageDate);
    };

    // Join chat room and listen to messages
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        console.log('💬 ChatInterface - isConnected:', isConnected);

        if (!isConnected || !user) {
            console.log('⏳ Waiting for socket connection and user...');
            return;
        }

        const roomId = user.role === 'admin' ? (activeRoomId || currentUserId) : userRoomId;
        historyCursorRef.current = undefined;
        hasMoreHistoryRef.current = false;
        isLoadingOlderMessagesRef.current = false;
        pendingHistoryBeforeRef.current = null;
        pendingScrollRestoreRef.current = null;
        scrollToLatestRef.current = false;
        setIsLoadingOlderMessages(false);
        setMessages([]);
        if (user.role === 'admin') {
            socketService.getConversations(currentUserId, true);
        }

        if (roomId) {
            socketService.joinRoom(roomId);
            socketService.getHistory(roomId, CHAT_HISTORY_PAGE_SIZE);
            console.log('🔗 Joined room:', roomId, 'userId:', currentUserId);
        }

        const handleHistory = (data: any) => {
            if (data?.roomId && String(data.roomId) !== String(roomId)) return;
            const history = Array.isArray(data) ? data : data?.messages || data?.data || [];
            const normalized = history.map((item: any) => ({
                id: item._id || item.id || `msg-${Date.now()}-${Math.random()}`,
                _id: item._id, // Store MongoDB ID for updates
                userId: item.userId || item.senderId || item.authorId || currentUserId,
                username: item.username || item.senderUsername || item.authorName || 'Người dùng',
                content: item.content || '',
                type: item.type || 'text',
                orderId: item.orderId,
                replyTo: item.replyTo,
                progress: item.progress,
                currentStep: item.currentStep,
                totalSteps: item.totalSteps,
                timestamp: new Date(item.createdAt || item.timestamp || Date.now()),
                isAdmin: Boolean(item.isAdmin || item.role === 'admin'),
                status: item.status || 'sent',
                isRead: Boolean(item.isRead),
                reactions: item.reactions || [],
            }));
            const requestedBefore = data?.before || null;
            if (requestedBefore && requestedBefore !== pendingHistoryBeforeRef.current) return;

            if (requestedBefore) {
                pendingScrollRestoreRef.current = {
                    scrollHeight: messageListRef.current?.scrollHeight || 0,
                    scrollTop: messageListRef.current?.scrollTop || 0,
                };
                setMessages((currentMessages) => sortSelectedOrderMessages(
                    dedupeMessages([...normalized, ...currentMessages]),
                    selectedOrder?._id,
                ));
                pendingHistoryBeforeRef.current = null;
                isLoadingOlderMessagesRef.current = false;
                setIsLoadingOlderMessages(false);
            } else {
                scrollToLatestRef.current = true;
                setMessages(sortSelectedOrderMessages(dedupeMessages(normalized), selectedOrder?._id));
            }

            historyCursorRef.current = normalized.length
                ? new Date(normalized[0].timestamp).toISOString()
                : historyCursorRef.current;
            hasMoreHistoryRef.current = Boolean(data?.hasMore);
        };

        const handleConversations = (data: any) => {
            const list = Array.isArray(data) ? data : data?.data || data?.conversations || [];
            const mapped = list.map((item: any) => {
                const roomIdValue = item.roomId || item._id || item.userId || '';
                const participantName = item.displayName || item.participantFullName || item.participantUsername || item.userName || item.username || item.user?.username || item.participantName || item.participant?.username || item.roomName || '';
                const lastSenderUsername = item.lastSenderUsername || item.senderUsername || '';
                const participantId = item.participantId || (String(roomIdValue).startsWith('user_') ? String(roomIdValue).slice(5) : roomIdValue);

                const sanitizeName = (name?: string) => {
                    if (!name) return '';
                    const trimmed = String(name).trim();
                    if (!trimmed) return '';
                    const normalized = trimmed.toLowerCase();
                    if (normalized === 'admin' || normalized === 'system' || normalized === user?.username?.toLowerCase()) {
                        return '';
                    }
                    return trimmed;
                };

                const chosenName = [participantName, lastSenderUsername]
                    .map((name) => sanitizeName(name))
                    .find(Boolean) || roomIdValue;

                const unreadCount = Number(item.unreadCount || item.unread || 0);
                const hasUnread = Boolean(item.hasUnread ?? unreadCount > 0);

                return {
                    roomId: roomIdValue,
                    participantId,
                    participantName: chosenName,
                    participantUsername: item.participantUsername || item.username || '',
                    participantRole: item.participantRole || item.role || item.user?.role || 'user',
                    lastMessage: item.lastMessage || item.lastText || '',
                    lastSenderUsername: chosenName,
                    unreadCount,
                    hasUnread,
                };
            });

            setConversations(mapped.map((room: {
                roomId: string;
                participantId?: string;
                participantName?: string;
                participantUsername?: string;
                participantRole?: string;
                lastMessage?: string;
                lastSenderUsername?: string;
                unreadCount: number;
                hasUnread?: boolean;
            }) => room));

            if (!activeRoomId && list.length > 0) {
                setActiveRoomId(list[0].roomId || currentUserId);
            }
        };

        const handleMessage = (message: any) => {
            console.log('💬 New message:', message);

            const incomingUserId = message.userId || message.senderId || message.authorId || message._userId;
            const incomingUsername = message.username || message.senderUsername || message.authorName || 'Người dùng';
            const nextMessage: Message = {
                id: message._id || message.id || `msg-${Date.now()}-${Math.random()}`,
                _id: message._id, // Store MongoDB ID for updates
                userId: incomingUserId || currentUserId,
                username: incomingUsername,
                content: message.content || '',
                type: message.type || 'text',
                orderId: message.orderId,
                replyTo: message.replyTo,
                progress: message.progress,
                currentStep: message.currentStep,
                totalSteps: message.totalSteps,
                timestamp: new Date(message.createdAt || message.timestamp || Date.now()),
                isAdmin: Boolean(message.isAdmin || false),
                status: message.status || 'sent',
                isRead: Boolean(message.isRead),
                reactions: message.reactions || [],
            };

            const messageRoomId = message.roomId || message.room || message.conversationId || roomId;
            const roomMatch = messageRoomId ? String(messageRoomId) === String(roomId) : true;

            if (roomMatch) {
                const list = messageListRef.current;
                if (!list || list.scrollHeight - list.scrollTop - list.clientHeight < 100) {
                    scrollToLatestRef.current = true;
                }
                setMessages(prev => sortSelectedOrderMessages(dedupeMessages([...prev, nextMessage]), selectedOrder?._id));
            }
        };

        const handleMessageMarkedUnread = (data: { messageId: string; roomId: string }) => {
            if (data.roomId === roomId) {
                setMessages((currentMessages) => currentMessages.map((message) =>
                    message._id === data.messageId
                        ? { ...message, isRead: false, status: 'delivered' }
                        : message,
                ));
            }
            if (user.role === 'admin') socketService.getConversations(currentUserId, true);
        };

        const handleMessageReactions = (data: { messageId: string; roomId: string; reactions: MessageReaction[] }) => {
            if (data.roomId !== roomId) return;
            setMessages((currentMessages) => currentMessages.map((message) =>
                message._id === data.messageId
                    ? { ...message, reactions: data.reactions }
                    : message,
            ));
        };

        socketService.on('chat:history', handleHistory);
        socketService.on('conversations:list', handleConversations);
        socketService.on('chat:message', handleMessage);
        socketService.on('chat:unread', handleMessageMarkedUnread);
        socketService.on('chat:message:reactions', handleMessageReactions);

        const isVisibleToCurrentUser = (order: Pick<Order, 'userId'>) =>
            user.role === 'admin' || String(order.userId) === String(currentUserId);

        const handleOrderCreated = (order: Order) => {
            console.log('Order created:', order);
            setOrderCountRefreshToken((current) => current + 1);
            if (!isVisibleToCurrentUser(order)) return;
            setOrders(prev => [order, ...prev.filter((existing) => existing._id !== order._id)]);
        };

        const handleOrderUpdated = (order: Order) => {
            console.log('Order updated:', order);
            setOrderCountRefreshToken((current) => current + 1);
            if (!isVisibleToCurrentUser(order)) {
                setOrders(prev => prev.filter(o => o._id !== order._id));
                if (selectedOrder?._id === order._id) setSelectedOrder(null);
                return;
            }
            setOrders(prev => prev.map(o => o._id === order._id ? order : o));
            setSelectedOrder(current => current?._id === order._id ? { ...current, ...order } : current);
        };

        const handleOrderStatus = (data: any) => {
            console.log('Order status received:', data);
            setOrderCountRefreshToken((current) => current + 1);
            // Update selectedOrder if it's the same order
            if (data.orderId && selectedOrder?._id === data.orderId) {
                const updatedOrder: Order = {
                    _id: data.orderId,
                    code: data.code,
                    userId: data.userId || selectedOrder?._id || '',
                    username: data.username || selectedOrder?.username || 'Không xác định',
                    status: data.status,
                    progress: data.progress || 0,
                    currentStep: data.currentStep || 0,
                    totalSteps: data.totalSteps || 8,
                    createdAt: selectedOrder?.createdAt || new Date(),
                    updatedAt: new Date(),
                };
                setSelectedOrder(updatedOrder);
                setOrders(prev => prev.map(o => o._id === data.orderId ? updatedOrder : o));
                
                // Also update the progress message in messages array
                setMessages(prev => prev.map(msg => {
                    if (msg.type === 'order' && msg.orderId === data.orderId) {
                        return {
                            ...msg,
                            progress: data.progress || 0,
                            currentStep: data.currentStep || 0,
                            totalSteps: data.totalSteps || 8,
                        };
                    }
                    return msg;
                }));
            }
        };

        const handleChatOrderStatus = (data: any) => {
            setMessages((currentMessages) => currentMessages.map((message) =>
                message.type === 'order' && message.orderId === data.orderId
                    ? {
                        ...message,
                        orderStatus: data.status,
                        progress: data.progress,
                        currentStep: data.currentStep,
                        totalSteps: data.totalSteps,
                    }
                    : message,
            ));
        };

        const handleOrderStepDone = (data: any) => {
            console.log('Step done:', data);
            addMessage({
                id: `msg-${Date.now()}`,
                userId: 'system',
                username: 'System',
                content: `✅ ${data.message}`,
                type: 'text',
                timestamp: new Date(),
                isAdmin: true,
            });
        };

        const handleOrderError = (error: any) => {
            console.log('Order error:', error);
            addMessage({
                id: `msg-${Date.now()}`,
                userId: 'system',
                username: 'System',
                content: `❌ ${error.message || 'Đã xảy ra lỗi với mã chung sức'}`,
                type: 'text',
                timestamp: new Date(),
                isAdmin: true,
            });
        };

        const handleMessageUpdated = (updatedMessage: any) => {
            console.log('Message updated (order progress):', updatedMessage);
            // Update the message in the list with new progress
            setMessages(prev => {
                const index = prev.findIndex(m => {
                    // Match by MongoDB _id first (most reliable)
                    if (updatedMessage._id && m._id === updatedMessage._id) return true;
                    // Fallback to id comparison
                    if (m.id === updatedMessage._id || m.id === updatedMessage.id) return true;
                    // Last resort: match by type and orderId for order messages
                    if (m.type === 'order' && updatedMessage.orderId && m.orderId === updatedMessage.orderId) {
                        return true;
                    }
                    return false;
                });
                
                if (index !== -1) {
                    const updated = [...prev];
                    updated[index] = {
                        ...updated[index],
                        content: updatedMessage.content,
                        progress: updatedMessage.progress,
                        currentStep: updatedMessage.currentStep,
                        totalSteps: updatedMessage.totalSteps,
                    };
                    console.log('✅ Message updated at index:', index, updated[index]);
                    return sortSelectedOrderMessages(dedupeMessages(updated), selectedOrder?._id);
                } else {
                    console.warn('⚠️ Message not found for update:', updatedMessage._id, 'Current messages:', prev.map(m => ({ id: m.id, _id: m._id, type: m.type })));
                }
                return prev;
            });
        };

        socketService.on('order:created', handleOrderCreated);
        socketService.on('order:recreated', handleOrderCreated);
        socketService.on('order:updated', handleOrderUpdated);
        socketService.on('order:status', handleOrderStatus);
        socketService.on('chat:order:status', handleChatOrderStatus);
        socketService.on('order:step:done', handleOrderStepDone);
        socketService.on('order:error', handleOrderError);
        socketService.on('chat:message:updated', handleMessageUpdated);

        return () => {
            socketService.off('chat:message', handleMessage);
            socketService.off('chat:history', handleHistory);
            socketService.off('conversations:list', handleConversations);
            socketService.off('chat:unread', handleMessageMarkedUnread);
            socketService.off('chat:message:reactions', handleMessageReactions);
            socketService.off('order:created', handleOrderCreated);
            socketService.off('order:recreated', handleOrderCreated);
            socketService.off('order:updated', handleOrderUpdated);
            socketService.off('order:status', handleOrderStatus);
            socketService.off('chat:order:status', handleChatOrderStatus);
            socketService.off('order:step:done', handleOrderStepDone);
            socketService.off('order:error', handleOrderError);
            socketService.off('chat:message:updated', handleMessageUpdated);
        };
    }, [isConnected, user, userId, currentUserId, activeRoomId]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
                setIsAccountMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const dedupeMessages = (items: Message[]) => {
        const seen = new Set<string>();

        return items.filter((item) => {
            const idValue = item.id || `${item.userId}-${item.content}-${new Date(item.timestamp).getTime()}`;
            if (seen.has(idValue)) {
                return false;
            }
            seen.add(idValue);
            return true;
        });
    };

    const sortSelectedOrderMessages = (items: Message[], orderId?: string) => {
        if (!orderId) return items;

        const selectedOrderMessages = items.filter(
            (item) => item.type === 'order' && item.orderId === orderId,
        );
        const otherMessages = items.filter(
            (item) => !(item.type === 'order' && item.orderId === orderId),
        );

        const orderedSelected = [...selectedOrderMessages].sort((a, b) => {
            const timeA = new Date(a.timestamp).getTime();
            const timeB = new Date(b.timestamp).getTime();
            return timeB - timeA;
        });

        return [...orderedSelected, ...otherMessages];
    };

    const focusSelectedOrderMessage = (orderId?: string) => {
        if (!orderId) return;

        const targetMessage = [...messages].reverse().find(
            (item) => item.type === 'order' && item.orderId === orderId,
        );

        if (!targetMessage) return;

        const key = targetMessage._id || targetMessage.id;
        const el = messageRefs.current[key];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-2', 'ring-blue-400');
            window.setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400'), 1200);
        }
    };

    useLayoutEffect(() => {
        const list = messageListRef.current;
        if (!list) return;

        const previousPosition = pendingScrollRestoreRef.current;
        if (previousPosition) {
            list.scrollTop = previousPosition.scrollTop + list.scrollHeight - previousPosition.scrollHeight;
            pendingScrollRestoreRef.current = null;
            return;
        }

        if (scrollToLatestRef.current) {
            list.scrollTop = list.scrollHeight;
            scrollToLatestRef.current = false;
            return;
        }

        if (selectedOrder?._id) {
            focusSelectedOrderMessage(selectedOrder._id);
        }
    }, [messages, selectedOrder?._id]);

    const handleMessageListScroll = () => {
        const list = messageListRef.current;
        const before = historyCursorRef.current;
        if (list) {
            setShowScrollToLatest(list.scrollHeight - list.scrollTop - list.clientHeight > 160);
        }
        if (
            !list
            || list.scrollTop > 60
            || !before
            || !hasMoreHistoryRef.current
            || isLoadingOlderMessagesRef.current
        ) {
            return;
        }

        pendingHistoryBeforeRef.current = before;
        pendingScrollRestoreRef.current = {
            scrollHeight: list.scrollHeight,
            scrollTop: list.scrollTop,
        };
        isLoadingOlderMessagesRef.current = true;
        setIsLoadingOlderMessages(true);
        const historyRoomId = user?.role === 'admin'
            ? (activeRoomId || currentUserId)
            : userRoomId;
        socketService.getHistory(historyRoomId, CHAT_HISTORY_PAGE_SIZE, before);
    };

    const scrollToLatestMessage = () => {
        const list = messageListRef.current;
        if (!list) return;
        list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
        setShowScrollToLatest(false);
    };

    const addMessage = (message: Message) => {
        const list = messageListRef.current;
        if (!list || list.scrollHeight - list.scrollTop - list.clientHeight < 100) {
            scrollToLatestRef.current = true;
        }
        setMessages(prev => sortSelectedOrderMessages(dedupeMessages([...prev, message]), selectedOrder?._id));
    };

    const handleLogout = () => {
        logout();
        setIsAccountMenuOpen(false);
    };

    const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!user?._id) return;
        setPasswordError('');
        setPasswordMessage('');
        if (passwordForm.newPassword.length < 6) {
            setPasswordError('Mật khẩu mới phải có ít nhất 6 ký tự.');
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordError('Mật khẩu xác nhận không khớp.');
            return;
        }

        setIsChangingPassword(true);
        try {
            await apiService.post(`/users/${user._id}/password`, {
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
            });
            setPasswordMessage('Đổi mật khẩu thành công.');
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (requestError: any) {
            setPasswordError(requestError.response?.data?.message || 'Không thể đổi mật khẩu. Vui lòng thử lại.');
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        const touch = event.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
        const start = touchStartRef.current;
        touchStartRef.current = null;
        if (!start) return;

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - start.x;
        const deltaY = touch.clientY - start.y;
        if (Math.abs(deltaX) < 60 || Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;

        if (deltaX > 0 && !isSidebarOpen) {
            setIsSidebarOpen(true);
        } else if (deltaX < 0 && isSidebarOpen) {
            setIsSidebarOpen(false);
        }
    };

    const handleSendMessage = () => {
        if (!inputMessage.trim()) return;

        const savedUser = localStorage.getItem('user_data');
        const currentUser = user || (savedUser && savedUser !== 'undefined' && savedUser !== 'null' ? JSON.parse(savedUser) : null);

        if (!currentUser || !currentUser._id) {
            console.warn('⚠️ Không thể gửi tin nhắn: chưa tải được thông tin người dùng');
            return;
        }

        const roomId = currentUser.role === 'admin' ? (activeRoomId || currentUser._id) : `user_${currentUser._id}`;

        console.log('📤 Sending message to room:', roomId, 'user:', currentUser);
        socketService.sendReplyMessage(inputMessage.trim(), roomId, replyingTo?._id);

        setInputMessage('');
        setReplyingTo(null);
        setMessageActionError('');
    };

    const handleSelectRoom = (roomId: string) => {
        setActiveRoomId(roomId);
        setConversations(prev => prev.map((room) =>
            room.roomId === roomId ? { ...room, unreadCount: 0 } : room
        ));
        setMessages([]);
        historyCursorRef.current = undefined;
        hasMoreHistoryRef.current = false;
        pendingHistoryBeforeRef.current = null;
        pendingScrollRestoreRef.current = null;
        scrollToLatestRef.current = false;
        setReplyingTo(null);
        setMessageActionError('');
    };

    const handleMarkMessageUnread = async (message: Message) => {
        if (!message._id) {
            setMessageActionError('Không thể đánh dấu tin nhắn này vì thiếu mã tin nhắn.');
            return;
        }
        setMarkingUnreadId(message._id);
        setMessageActionError('');
        try {
            await socketService.markMessageUnread(message._id);
            setMessages((currentMessages) => currentMessages.map((item) =>
                item._id === message._id ? { ...item, isRead: false, status: 'delivered' } : item,
            ));
        } catch (error) {
            setMessageActionError(error instanceof Error ? error.message : 'Không thể đánh dấu tin nhắn chưa đọc.');
        } finally {
            setMarkingUnreadId(null);
        }
    };

    const scrollToMessage = (messageId?: string) => {
        if (!messageId) return;
        messageRefs.current[messageId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const isAdmin = user?.role === 'admin';
    const isPartner = user?.role === 'partner';
    const canCancelOrders = user?.role === 'admin' || user?.role === 'partner' || user?.role === 'user';
    const outstandingAmount = partnerOutstanding.unpaidTotal;
    const outstandingSteps = partnerOutstanding.outstandingSteps;
    const selectedConversation = conversations.find((room) => room.roomId === activeRoomId);
    const activeRoomLabel = isAdmin
        ? (selectedConversation?.participantName || selectedConversation?.lastSenderUsername || activeRoomId || 'Phòng trò chuyện')
        : (selectedOrder ? `Mã chung sức ${selectedOrder.code}` : 'Tin nhắn');

    const handleCreateOrder = async () => {
        setOrderEntryError('');
        setOrderEntryMessage('');
        if (maintenanceEnabled !== false) {
            setOrderEntryError(maintenanceEnabled
                ? 'Hệ thống đang bảo trì, tạm thời không thể tạo mã chung sức.'
                : maintenanceStatusError || 'Chưa xác định được trạng thái hệ thống, vui lòng thử lại.');
            return;
        }
        if (!orderCode.trim()) {
            setOrderEntryError('Vui lòng nhập mã chung sức.');
            return;
        }

        const ordersToCreate = parseBulkOrderInput(orderCode, orderSteps);

        const invalidLine = ordersToCreate.find((order) => !order.hasValidFormat);
        if (invalidLine || ordersToCreate.length === 0) {
            setOrderEntryError('Định dạng chưa đúng. Mỗi dòng nhập mã hoặc mã|số lượng (số lượng phải là số nguyên dương).');
            return;
        }

        const largeStepOrder = ordersToCreate.find((order) => order.totalSteps >= 50);
        if (largeStepOrder) {
            const confirmed = window.confirm(
                `Mã chung sức ${largeStepOrder.code} có ${largeStepOrder.totalSteps} bước. Bạn có chắc chắn muốn tiếp tục tạo không?`,
            );
            if (!confirmed) {
                return;
            }
        }

        setIsLoading(true);
        try {
            const result = await socketService.createOrders(
                ordersToCreate.map(({ code, totalSteps }) => ({ code, totalSteps })),
                Boolean(largeStepOrder),
            );
            setOrderCode('');
            setOrderEntryMessage(`Đã tạo thành công ${result.orders?.length || ordersToCreate.length} mã chung sức.`);
            refreshPartnerOutstanding();
        } catch (requestError: any) {
            setOrderEntryError(requestError.message || 'Không tạo được mã chung sức. Vui lòng thử lại.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStepOrder = (orderId: string) => {
        socketService.stepOrder(orderId);
    };

    const handleProcessOrder = (orderId: string) => {
        socketService.processOrder(orderId);
    };

    const handleResetOrder = (orderId: string) => {
        socketService.resetOrder(orderId);
    };

    const handleCancelOrder = async (order: Order) => {
        const feeDescription = (order.unpaidAmount || 0) > 0
            ? 'Phí hủy 50 đ sẽ được giữ lại thành công nợ.'
            : (order.paidAmount || 0) > 0
                ? 'Tiền hoàn về ví sẽ trừ phí hủy 50 đ.'
                : 'Mã chung sức này không phát sinh hoàn tiền hoặc công nợ.';
        if (!window.confirm(`Hủy mã chung sức ${order.code}? ${feeDescription}`)) return;

        setCancellingOrderId(order._id);
        try {
            const response = await apiService.patch<{ data: Order }>(`/orders/${order._id}/cancel`, {});
            setOrders((currentOrders) => currentOrders.map((item) =>
                item._id === order._id ? response.data : item,
            ));
            setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? response.data : currentOrder);
            refreshPartnerOutstanding();
        } catch (error: any) {
            window.alert(error.response?.data?.message || 'Không thể hủy mã chung sức.');
        } finally {
            setCancellingOrderId(null);
        }
    };

    const handleRecreateOrder = (order: Order) => {
        const failedSteps = order.failedSteps || 0;
        if (!failedSteps) return;
        if (window.confirm(`Tạo lại mã chung sức ${order.code} với ${failedSteps} lần lỗi?`)) {
            socketService.recreateOrder(order._id, failedSteps);
        }
    };

    const handleRequestOrderRedo = async (order: Order, reason: string) => {
        const response = await socketService.requestCompletedOrderRedo(order._id, reason);
        if (!response.order) throw new Error('Máy chủ không trả về thông tin yêu cầu làm lại.');
        const updatedOrder = response.order;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? updatedOrder : currentOrder);
        return updatedOrder;
    };

    const handleConfirmCompletedOrder = async (order: Order) => {
        const response = await apiService.patch<{ data: Order }>(`/orders/${order._id}/confirm`, {});
        const updatedOrder = response.data;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? updatedOrder : currentOrder);
        return updatedOrder;
    };

    const handleCancelOrderConfirmation = async (order: Order) => {
        const response = await apiService.patch<{ data: Order }>(`/orders/${order._id}/confirm/cancel`, {});
        const updatedOrder = response.data;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? updatedOrder : currentOrder);
        return updatedOrder;
    };

    const handleRequestOrderRefund = async (order: Order, reason: string) => {
        const response = await socketService.requestCompletedOrderRefund(order._id, reason);
        if (!response.order) throw new Error('Máy chủ không trả về thông tin yêu cầu hoàn tiền.');
        const updatedOrder = response.order;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? updatedOrder : currentOrder);
        return updatedOrder;
    };

    const handleApproveOrderRedo = async (orderOrId: Order | string) => {
        const orderId = typeof orderOrId === 'string' ? orderOrId : orderOrId._id;
        const response = await socketService.approveCompletedOrderRedo(orderId);
        if (!response.order) throw new Error('Máy chủ không trả về mã chung sức làm lại.');
        const redoOrder = response.order;
        setOrders((currentOrders) => [
            redoOrder,
            ...currentOrders.map((item) => item._id === orderId
                ? { ...item, redoRequestStatus: 'approved' as const }
                : item),
        ].slice(0, 50));
        setSelectedOrder((currentOrder) => currentOrder?._id === orderId
            ? { ...currentOrder, redoRequestStatus: 'approved' }
            : currentOrder);
    };

    const handleApproveOrderRefund = async (orderOrId: Order | string) => {
        const orderId = typeof orderOrId === 'string' ? orderOrId : orderOrId._id;
        const response = await socketService.approveCompletedOrderRefund(orderId);
        if (!response.order) throw new Error('Máy chủ không trả về mã chung sức đã hoàn tiền.');
        const updatedOrder = response.order;
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === orderId ? updatedOrder : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === orderId ? updatedOrder : currentOrder);
    };

    const handleApproveRedoFromMessage = async (message: Message) => {
        if (!message.orderId || !message._id) return;
        const messageId = message._id;
        setApprovingRedoMessageId(message._id);
        setMessageActionError('');
        try {
            await handleApproveOrderRedo(message.orderId);
            setApprovedRedoMessageIds((currentIds) => [...currentIds, messageId]);
        } catch (error) {
            setMessageActionError(error instanceof Error ? error.message : 'Không thể duyệt yêu cầu làm lại.');
        } finally {
            setApprovingRedoMessageId(null);
        }
    };

    const handleApproveRefundFromMessage = async (message: Message) => {
        if (!message.orderId || !message._id) return;
        const messageId = message._id;
        setApprovingRefundMessageId(messageId);
        setMessageActionError('');
        try {
            await handleApproveOrderRefund(message.orderId);
            setApprovedRefundMessageIds((currentIds) => [...currentIds, messageId]);
        } catch (error) {
            setMessageActionError(error instanceof Error ? error.message : 'Không thể duyệt yêu cầu hoàn tiền.');
        } finally {
            setApprovingRefundMessageId(null);
        }
    };

    const handleToggleMessageReaction = async (message: Message, emoji: string) => {
        if (!message._id || reactingMessageId) return;
        setReactingMessageId(message._id);
        setMessageActionError('');
        try {
            await socketService.toggleMessageReaction(message._id, emoji);
            setActiveReactionMessageId(null);
        } catch (error) {
            setMessageActionError(error instanceof Error ? error.message : 'Không thể cập nhật cảm xúc.');
        } finally {
            setReactingMessageId(null);
        }
    };

    const handleToggleIntegrationPriority = async (order: Order) => {
        setUpdatingPriorityOrderId(order._id);
        try {
            const response = await apiService.patch<{ data: Order }>(
                `/orders/${order._id}/integration-priority`,
                { enabled: !order.integrationPriority },
            );
            setOrders((currentOrders) => currentOrders.map((item) =>
                item._id === order._id ? response.data : item,
            ));
            setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? response.data : currentOrder);
            refreshPartnerOutstanding();
        } catch (error: any) {
            window.alert(error.response?.data?.message || 'Không thể cập nhật ưu tiên mã chung sức.');
        } finally {
            setUpdatingPriorityOrderId(null);
        }
    };

    const handleUpdatePendingOrder = async (order: Order, code: string, totalSteps: number) => {
        const response = await apiService.patch<{ data: Order }>(
            `/orders/${order._id}/pending-details`,
            { code, totalSteps },
        );
        setOrders((currentOrders) => currentOrders.map((item) =>
            item._id === order._id ? response.data : item,
        ));
        setSelectedOrder((currentOrder) => currentOrder?._id === order._id ? response.data : currentOrder);
        return response.data;
    };

    const handleSelectOrder = (order: Order) => {
        console.log('📦 Selecting order:', order._id, order.code);

        setSelectedOrder(order);
        setMessages(prev => sortSelectedOrderMessages(prev, order._id));

        const currentUserRoomId = `user_${String(currentUserId)}`;
        socketService.getOrderStatus(order._id);

        setTimeout(() => {
            const orderMessages = document.querySelectorAll('[data-order-id="' + order._id + '"]');
            const latestOrderMessage = orderMessages.length ? orderMessages[orderMessages.length - 1] : null;
            if (latestOrderMessage) {
                (latestOrderMessage as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 350);

        console.log('📦 Order selected, loading history from room:', currentUserRoomId);
    };

    const handleOpenOrderFromTable = (order: Order) => {
        handleSelectOrder(order);
    };

    const copySelectedOrderCode = async () => {
        if (!selectedOrder) return;
        try {
            await navigator.clipboard.writeText(selectedOrder.code);
            setIsOrderCodeCopied(true);
            window.setTimeout(() => setIsOrderCodeCopied(false), 1500);
        } catch (error) {
            console.error('Không sao chép được mã chung sức:', error);
            window.alert('Không thể sao chép mã chung sức. Vui lòng thử lại.');
        }
    };

    const getStatusColor = (status: OrderStatus) => {
        const colors = {
            [OrderStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
            [OrderStatus.CODE_RECEIVED]: 'bg-indigo-100 text-indigo-800',
            [OrderStatus.PROCESSING]: 'bg-blue-100 text-blue-800',
            [OrderStatus.COMPLETED]: 'bg-green-100 text-green-800',
            [OrderStatus.ERROR]: 'bg-red-100 text-red-800',
            [OrderStatus.CANCELED]: 'bg-gray-100 text-gray-700',
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusEmoji = (status: OrderStatus) => {
        const emojis = {
            [OrderStatus.PENDING]: '⏳',
            [OrderStatus.CODE_RECEIVED]: '📌',
            [OrderStatus.PROCESSING]: '🔄',
            [OrderStatus.COMPLETED]: '✅',
            [OrderStatus.ERROR]: '❌',
            [OrderStatus.CANCELED]: '🚫',
        };
        return emojis[status] || '📋';
    };

    return (
        <div
            className={`chat-theme relative flex h-[100dvh] overflow-hidden bg-gray-100 ${isDarkTheme ? 'chat-theme-dark' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {activeNav === 'home' && (isAdmin ? (
                <div className={`${isSidebarOpen ? 'fixed inset-y-0 left-0 z-30 flex md:flex' : 'hidden'} w-80 shrink-0 flex-col border-r border-gray-200 bg-white md:static md:z-auto`}>
                    <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between gap-2">
                            <h2 className="text-lg font-semibold">💬 Phòng trò chuyện</h2>
                            <button
                                type="button"
                                onClick={() => setIsSidebarOpen(false)}
                                className="rounded-lg px-2 py-1 text-xl leading-none text-gray-500 hover:bg-gray-100"
                                aria-label="Đóng danh sách"
                                title="Đóng danh sách"
                            >
                                ‹
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                        {conversations.length === 0 ? (
                            <div className="p-4 text-sm text-gray-500">Chưa có cuộc trò chuyện nào</div>
                        ) : (
                            conversations.map((room) => (
                                <button
                                    key={room.roomId}
                                    type="button"
                                    onClick={() => handleSelectRoom(room.roomId)}
                                    className={`w-full text-left p-3 mb-2 rounded-lg transition-all ${
                                        activeRoomId === room.roomId
                                            ? 'bg-blue-50 border border-blue-200'
                                            : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                                    }`}
                                >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="ml-auto flex items-center gap-2">
                                                    <span className="font-semibold truncate">
                                                        {room.participantName || room.participantUsername || 'Người dùng'}
                                                    </span>
                                                    <span className="shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-600">
                                                        {getRoleLabel(room.participantRole)}
                                                    </span>
                                                </div>
                                                <span className="block truncate text-xs text-gray-400" title={room.participantId || room.roomId}>
                                                    ID: {room.participantId || room.roomId}
                                                </span>
                                            </div>
                                        {(room.unreadCount || 0) > 0 ? (
                                            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                                                {room.unreadCount}
                                            </span>
                                        ) : null}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1 truncate">
                                        {room.lastMessage || 'Chưa có tin nhắn nào'}
                                    </p>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            ) : (
                <div className={`${isSidebarOpen ? 'fixed inset-y-0 left-0 z-30 flex md:flex' : 'hidden'} w-80 shrink-0 flex-col border-r border-gray-200 bg-white md:static md:z-auto`}>
                    <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between gap-2">
                            <h2 className="text-lg font-semibold">📋 Mã chung sức</h2>
                            <button
                                type="button"
                                onClick={() => setIsSidebarOpen(false)}
                                className="rounded-lg px-2 py-1 text-xl leading-none text-gray-500 hover:bg-gray-100"
                                aria-label="Đóng danh sách"
                                title="Đóng danh sách"
                            >
                                ‹
                            </button>
                        </div>
                        <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                            <div className="relative col-span-2">
                                <textarea
                                    value={orderCode}
                                    onChange={(event) => {
                                        setOrderCode(event.target.value);
                                        setOrderEntryError('');
                                        setOrderEntryMessage('');
                                    }}
                                    placeholder="Nhập mã chung sức\nVí dụ: bYG3qj5LuZ"
                                    rows={3}
                                    aria-label="Mã chung sức và số lần"
                                    className="min-w-0 w-full resize-y rounded-lg border border-gray-200 px-3 py-2 pr-10 font-mono text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    type="button"
                                    ref={bulkOrderHelpButtonRef}
                                    onClick={() => setShowBulkOrderHelp((visible) => !visible)}
                                    className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-xs font-bold text-gray-500 hover:border-blue-500 hover:text-blue-600"
                                    aria-label="Cách nhập nhiều mã chung sức"
                                    title="Cách nhập nhiều mã chung sức"
                                >
                                    ?
                                </button>
                                {showBulkOrderHelp && (
                                    <div ref={bulkOrderHelpPopupRef} className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800 shadow-lg">
                                        <p className="font-semibold">Cách nhập nhiều mã chung sức</p>
                                        <p className="mt-1">Mỗi dòng nhập một mã dài đúng 10 ký tự. Có thể dán nguyên văn tin nhắn; mã sẽ được lấy từ phần nằm giữa hai dấu --.</p>
                                        <p className="mt-1">Số lượng sau | là tùy chọn; nếu bỏ qua thì mặc định là 1.</p>
                                        <p className="mt-1 font-mono">Mã mời của tôi là --bYG3qj5LuZ--</p>
                                        <p className="font-mono">bYG3qj5LuZ</p>
                                        <p className="font-mono">bYG3qj5LuZ|2</p>
                                    </div>
                                )}
                            </div>
                            <select
                                value={orderSteps}
                                onChange={(event) => setOrderSteps(Number(event.target.value))}
                                className="min-w-0 rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                                aria-label="Số lần"
                            >
                                {orderPricing.length > 0 ? orderPricing.map((item) => (
                                    <option key={item.totalSteps} value={item.totalSteps}>
                                        {item.title ? `${item.title} · ` : ''}{item.totalSteps} lần · {item.discountPercent ? `${(item.originalPrice ?? item.price).toLocaleString('vi-VN')} → ${(item.discountedPrice ?? item.price).toLocaleString('vi-VN')}` : item.price.toLocaleString('vi-VN')} đ
                                    </option>
                                )) : <option value={1}>Chưa có bảng giá</option>}
                            </select>
                            <button
                                type="button"
                                onClick={handleCreateOrder}
                                disabled={isLoading}
                                className="min-w-[72px] rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isLoading ? 'Đang tạo...' : 'Tạo'}
                            </button>
                            {selectedOrderPricing && (
                                <p className="col-span-2 text-xs text-gray-600">
                                    Giá mã chung sức:{' '}
                                    {selectedOrderPricing.discountPercent ? (
                                        <>
                                            <del className="mr-1 text-gray-400">{(selectedOrderPricing.originalPrice ?? selectedOrderPricing.price).toLocaleString('vi-VN')} đ</del>
                                            <strong className="text-green-700">{(selectedOrderPricing.discountedPrice ?? selectedOrderPricing.price).toLocaleString('vi-VN')} đ</strong>
                                            <span className="ml-1 text-green-700">(VIP -{selectedOrderPricing.discountPercent}%)</span>
                                        </>
                                    ) : <strong>{selectedOrderPricing.price.toLocaleString('vi-VN')} đ</strong>}
                                </p>
                            )}
                            {orderPriceEstimate && (
                                <p className="col-span-2 text-xs text-gray-600">
                                    Tổng tiền ước tính ({orderPriceEstimate.orderCount} mã chung sức):{' '}
                                    {orderPriceEstimate.originalTotal !== orderPriceEstimate.payableTotal && (
                                        <del className="mr-1 text-gray-400">{orderPriceEstimate.originalTotal.toLocaleString('vi-VN')} đ</del>
                                    )}
                                    <strong className={orderPriceEstimate.originalTotal !== orderPriceEstimate.payableTotal ? 'text-green-700' : ''}>
                                        {orderPriceEstimate.payableTotal.toLocaleString('vi-VN')} đ
                                    </strong>
                                </p>
                            )}
                            {(orderEntryError || orderEntryMessage) && (
                                <p role={orderEntryError ? 'alert' : 'status'} className={`col-span-2 text-xs ${orderEntryError ? 'text-red-600' : 'text-green-700'}`}>
                                    {orderEntryError || orderEntryMessage}
                                </p>
                            )}
                        </div>
                    </div>
                    <div
                        ref={orderListRef}
                        onScroll={handleOrderListScroll}
                        className="flex-1 overflow-y-auto p-2"
                    >
                        {orders.length === 0 && isLoadingOrderPage ? (
                            <div className="p-4 text-center text-sm text-gray-500">Đang tải mã chung sức...</div>
                        ) : null}
                        {orders.map((order) => (
                            <div
                                key={order._id}
                                onClick={() => handleSelectOrder(order)}
                                className={`p-3 mb-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                                    selectedOrder?._id === order._id
                                        ? 'bg-blue-50 border-2 border-blue-300'
                                        : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                            >
                                <div className="flex justify-between items-center">
                                    <span className="font-medium">{order.code}</span>
                                    <div className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                                        {getStatusEmoji(order.status)} {getOrderStatusLabel(order.status)}
                                    </div>
                                </div>
                                {order.note && (
                                    <p className="mt-1 truncate text-xs text-gray-600" title={order.note}>
                                        Ghi chú: {order.note}
                                    </p>
                                )}
                                <div className="flex justify-between items-center mt-1 text-sm text-gray-500">
                                    <span>Tiến độ: {order.progress}%</span>
                                    <span>Lần {order.currentStep}/{order.totalSteps}</span>
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                    Đã chạy: {order.processedSteps || order.currentStep || 0} · Thành công: {Math.max(0, Math.max(order.processedSteps || 0, order.currentStep || 0) - (order.failedSteps || 0))} · Lỗi: {order.failedSteps || 0}
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                    <div
                                        className={`h-1.5 rounded-full transition-all duration-500 ${
                                            order.status === OrderStatus.ERROR ? 'bg-red-500' :
                                            order.status === OrderStatus.COMPLETED ? 'bg-green-500' :
                                            order.status === OrderStatus.CANCELED ? 'bg-gray-500' :
                                            'bg-blue-500'
                                        }`}
                                        style={{ width: `${order.progress}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                        {ordersLoadError ? (
                            <div className="p-3 text-center text-sm text-red-600">
                                <p>{ordersLoadError}</p>
                                <button
                                    type="button"
                                    onClick={() => void loadOrderPage(retryOrderPageFromStartRef.current)}
                                    className="mt-1 font-medium underline"
                                >
                                    Thử lại
                                </button>
                            </div>
                        ) : isLoadingOrderPage && orders.length > 0 ? (
                            <div className="p-3 text-center text-sm text-gray-500">Đang tải thêm mã chung sức...</div>
                        ) : !hasMoreOrders && orders.length > 0 ? (
                            <div className="p-3 text-center text-xs text-gray-400">Đã tải hết mã chung sức</div>
                        ) : null}
                    </div>
                </div>
            ))}

            {/* Main Chat Area */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                {/* Header */}
                <div className="bg-white border-b border-gray-200">
                    <div className="flex items-center gap-2 px-3 py-3 md:px-4">
                        {activeNav === 'home' ? (
                            <button
                                type="button"
                                onClick={() => setIsSidebarOpen((open) => !open)}
                                className="rounded-lg px-2 py-1 text-2xl leading-none text-gray-600 hover:bg-gray-100"
                                aria-label={isSidebarOpen ? 'Đóng danh sách' : 'Mở danh sách'}
                                title={isSidebarOpen ? 'Đóng danh sách' : 'Mở danh sách'}
                            >
                                {isSidebarOpen ? '‹' : '›'}
                            </button>
                        ) : null}
                        <nav className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-1 md:justify-start md:gap-2 md:overflow-visible md:pb-0">
                            {[
                                { key: 'orders', label: 'Vé Nhảy CS' },
                                { key: 'articles', label: 'Hướng dẫn' },
                                ...(!isAdmin ? [{ key: 'recharge', label: 'Nạp tiền' }] : []),
                                ...(isAdmin ? [{ key: 'admin-recharge', label: 'Duyệt nạp tiền' }] : []),
                                ...(isAdmin ? [{ key: 'admin-pricing', label: 'Quản lý giá' }] : []),
                                ...(isAdmin ? [{ key: 'admin-statistics', label: 'Thống kê user' }] : []),
                                ...(isAdmin ? [{ key: 'admin-maintenance', label: 'Bảo trì' }] : []),
                            ].map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => {
                                        setActiveNav(item.key as 'home' | 'orders' | 'articles' | 'recharge' | 'admin-recharge' | 'admin-pricing' | 'admin-statistics' | 'admin-maintenance');
                                        if (item.key === 'articles' || item.key === 'recharge' || item.key === 'admin-recharge' || item.key === 'admin-pricing' || item.key === 'admin-statistics' || item.key === 'admin-maintenance') setSelectedOrder(null);
                                    }}
                                    className={`shrink-0 whitespace-nowrap rounded-lg px-2.5 py-2 text-xs font-medium transition md:px-3 md:text-sm ${
                                        activeNav === item.key
                                            ? 'bg-blue-500 text-white shadow-sm'
                                            : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </nav>

                        <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setIsDarkTheme((dark) => !dark)}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-lg shadow-sm transition hover:bg-gray-100"
                            aria-label={isDarkTheme ? 'Chuyển sang giao diện sáng' : 'Chuyển sang giao diện tối'}
                            title={isDarkTheme ? 'Chuyển sang giao diện sáng' : 'Chuyển sang giao diện tối'}
                        >
                            {isDarkTheme ? '☀️' : '🌙'}
                        </button>
                        <div className="relative" ref={accountMenuRef}>
                            <button
                                type="button"
                                onClick={() => {
                                    const nextOpen = !isAccountMenuOpen;
                                    setIsAccountMenuOpen(nextOpen);
                                    if (nextOpen && !isAdmin) void loadRechargeHistory();
                                }}
                                className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
                                aria-label="Mở menu tài khoản"
                            >
                                <img
                                    src={user?.avatar || '/logo.jpg'}
                                    alt={displayName}
                                    className="h-full w-full object-cover"
                                />
                            </button>

                            {isAccountMenuOpen && (
                                <div className="absolute right-0 z-20 mt-2 w-80 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
                                    <div className="border-b border-gray-200 p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-bold text-white">
                                                <img
                                                    src={user?.avatar || '/logo.jpg'}
                                                    alt={displayName}
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-800">{displayName}</p>
                                                <p className="text-xs text-gray-500">@{user?.username}</p>
                                                <p className="text-xs text-gray-500">{getRoleLabel(user?.role)}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsChangePasswordOpen(true);
                                                setIsAccountMenuOpen(false);
                                                setPasswordError('');
                                                setPasswordMessage('');
                                            }}
                                            className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                        >
                                            <span>Thông tin tài khoản / Đổi mật khẩu</span>
                                            <span>👤</span>
                                        </button>

                                        <div className="mt-2 rounded-xl bg-gray-50 p-2">
                                            <div className="mb-2 flex items-center justify-between px-2 py-1">
                                                <span className="text-sm font-medium text-gray-700">Lịch sử nạp tiền</span>
                                                <span className="text-xs text-blue-500">{rechargeHistory.length} giao dịch</span>
                                            </div>
                                            <div className="space-y-2">
                                                {isRechargeHistoryLoading ? (
                                                    <p className="px-2 py-3 text-xs text-gray-500">Đang tải lịch sử...</p>
                                                ) : rechargeHistory.length === 0 ? (
                                                    <p className="px-2 py-3 text-xs text-gray-500">Chưa có giao dịch nạp tiền</p>
                                                ) : rechargeHistory.map((item) => (
                                                    <div
                                                        key={item.transactionId}
                                                        className="flex items-center justify-between rounded-lg bg-white px-2 py-2 shadow-sm"
                                                    >
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-700">{item.amount.toLocaleString('vi-VN')} đ</p>
                                                            <p className="text-[11px] text-gray-400">{formatRechargeDate(item.createdAt)}</p>
                                                        </div>
                                                        <span className={`rounded-full px-2 py-1 text-[10px] font-medium ${
                                                            item.status === 'success'
                                                                ? 'bg-green-100 text-green-700'
                                                                : item.status === 'failed' || item.status === 'cancelled' || item.status === 'expired'
                                                                    ? 'bg-red-100 text-red-700'
                                                                    : 'bg-yellow-100 text-yellow-700'
                                                        }`}>
                                                            {getRechargeStatusLabel(item.status)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="mt-3 flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50"
                                        >
                                            <span>Đăng xuất</span>
                                            <span>↩</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 px-3 pb-3 md:px-4 md:pb-4">
                        <div>
                            <div className="flex items-center gap-1.5">
                                <h1 className="text-[14px] font-semibold md:text-xl">
                                    {selectedOrder
                                        ? `Mã chung sức: ${selectedOrder.code}`
                                        : activeNav === 'home' ? (isAdmin ? 'Tin nhắn' : activeRoomLabel) : activeNav === 'orders' ? 'Mã chung sức' : activeNav === 'articles' ? 'Bài viết' : activeNav === 'admin-recharge' ? 'Duyệt nạp tiền' : activeNav === 'admin-pricing' ? 'Quản lý giá' : activeNav === 'admin-statistics' ? 'Thống kê user' : activeNav === 'admin-maintenance' ? 'Bảo trì' : 'Nạp tiền'}
                                </h1>
                                {selectedOrder && (
                                    <button
                                        type="button"
                                        onClick={() => void copySelectedOrderCode()}
                                        aria-label={`Sao chép mã chung sức ${selectedOrder.code}`}
                                        title={isOrderCodeCopied ? 'Đã sao chép' : 'Sao chép mã chung sức'}
                                        className="inline-flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                                    >
                                        {isOrderCodeCopied ? (
                                            <span aria-hidden="true" className="text-sm text-green-600">✓</span>
                                        ) : (
                                            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                                                <rect x="8" y="8" width="12" height="12" rx="2" />
                                                <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                            </div>
                            {selectedOrder && (
                                <>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedOrder.status)}`}>
                                            {getStatusEmoji(selectedOrder.status)} {selectedOrder.error === 'Cancelled' ? 'Đã hủy' : getOrderStatusLabel(selectedOrder.status)}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {selectedOrder.progress}% · Lần {selectedOrder.currentStep}/{selectedOrder.totalSteps}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            Đã chạy: {selectedOrder.processedSteps ?? selectedOrder.currentStep ?? 0}
                                            {' · '}Thành công: {Math.max(0, Math.max(selectedOrder.processedSteps || 0, selectedOrder.currentStep || 0) - (selectedOrder.failedSteps || 0))}
                                            {' · '}Lỗi: {selectedOrder.failedSteps ?? 0}
                                        </span>
                                    </div>
                                    {selectedOrder.status === OrderStatus.ERROR && (selectedOrder.error || selectedOrder.message) && (
                                        <p role="status" className="mt-2 max-w-3xl rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                            Lý do thất bại: {selectedOrder.error || selectedOrder.message}
                                        </p>
                                    )}
                                    {(isAdmin || isPartner) && selectedOrder.status === OrderStatus.PENDING && (
                                        <button
                                            type="button"
                                            onClick={() => void handleToggleIntegrationPriority(selectedOrder)}
                                            disabled={updatingPriorityOrderId === selectedOrder._id}
                                            aria-label={selectedOrder.integrationPriority ? 'Bỏ ưu tiên mã chung sức' : 'Ưu tiên mã chung sức'}
                                            className={`mt-2 rounded-lg border px-3 py-2 text-sm font-semibold transition disabled:cursor-wait disabled:opacity-50 ${selectedOrder.integrationPriority ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-gray-200 bg-white text-gray-700 hover:border-amber-300 hover:text-amber-800'}`}
                                        >
                                            {updatingPriorityOrderId === selectedOrder._id
                                                ? 'Đang cập nhật...'
                                                : selectedOrder.integrationPriority ? '★ Đang ưu tiên' : '☆ Ưu tiên mã này'}
                                        </button>
                                    )}
                                    {canCancelOrders && selectedOrder.status === OrderStatus.PENDING && (
                                        <button
                                            type="button"
                                            onClick={() => void handleCancelOrder(selectedOrder)}
                                            disabled={cancellingOrderId === selectedOrder._id}
                                            className="mt-2 ml-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-wait disabled:opacity-50"
                                            title="Hủy mã chung sức và áp dụng phí 50 đ"
                                        >
                                            {cancellingOrderId === selectedOrder._id ? 'Đang hủy...' : 'Hủy mã chung sức'}
                                        </button>
                                    )}
                                </>
                            )}
                            {activeNav === 'orders' && (
                                <>
                                    {orderCountError && (
                                        <p role="alert" className="mt-1 text-xs text-red-600">
                                            {orderCountError}
                                        </p>
                                    )}
                                    <p role="status" className="mt-1 text-xs text-gray-500">
                                        {isAdmin && !orderCountError && (
                                            <>
                                                Mã chung sức thành công: {successfulOrderCount === null ? 'Đang tải...' : successfulOrderCount.toLocaleString('vi-VN')}
                                                {' · '}
                                            </>
                                        )}
                                        Người đang truy cập web: {onlineUserCount === null ? 'Đang tải...' : onlineUserCount.toLocaleString('vi-VN')}
                                    </p>
                                </>
                            )}
                        </div>
                        {isPartner && outstandingAmount > 0 && !selectedOrder && (
                            <div className="flex shrink-0 items-center gap-2 rounded-lg bg-orange-50 px-2 py-1.5 text-right text-[11px] text-orange-700 md:gap-3 md:px-3 md:py-2 md:text-sm">
                                <div>
                                    <span className="block text-[10px] md:text-xs">Tổng chưa thanh toán</span>
                                    <strong className="text-[11px] md:text-sm">{outstandingAmount.toLocaleString('vi-VN')} đ</strong>
                                    <span className="block text-[10px] md:text-xs">{outstandingSteps.toLocaleString('vi-VN')} số lượt</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveNav('recharge')}
                                    className="rounded-md bg-orange-500 px-2 py-1.5 text-[10px] font-semibold text-white hover:bg-orange-600 md:px-3 md:py-2 md:text-sm"
                                >
                                    Thanh toán
                                </button>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            {selectedOrder && isAdmin && (
                                <>
                                    {selectedOrder.status === OrderStatus.ERROR && (selectedOrder.failedSteps || 0) > 0 && (
                                        <button
                                            onClick={() => handleRecreateOrder(selectedOrder)}
                                            className="px-3 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600"
                                            title="Tạo lại theo số lần lỗi"
                                        >
                                            🔁 Tạo lại ({selectedOrder.failedSteps} lần)
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleStepOrder(selectedOrder._id)}
                                        disabled={selectedOrder.status === OrderStatus.COMPLETED || selectedOrder.status === OrderStatus.ERROR || selectedOrder.status === OrderStatus.CANCELED}
                                        className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
                                        title="Chạy 1 lần"
                                    >
                                        ⏭️ Lần
                                    </button>
                                    <button
                                        onClick={() => handleProcessOrder(selectedOrder._id)}
                                        disabled={selectedOrder.status === OrderStatus.COMPLETED || selectedOrder.status === OrderStatus.ERROR || selectedOrder.status === OrderStatus.CANCELED}
                                        className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
                                        title="Chạy tất cả các lần"
                                    >
                                        ▶️ Chạy
                                    </button>
                                    <button
                                        onClick={() => handleResetOrder(selectedOrder._id)}
                                        disabled={selectedOrder.status === OrderStatus.COMPLETED}
                                        className="px-3 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 disabled:opacity-50"
                                        title="Đặt lại mã chung sức"
                                    >
                                        🔄 Đặt lại
                                    </button>
                                </>
                            )}
                            <span
                                role="status"
                                aria-label={isConnected ? 'Đang kết nối' : 'Mất kết nối'}
                                title={isConnected ? 'Đang kết nối' : 'Mất kết nối'}
                                className="inline-flex h-5 w-5 items-center justify-center"
                            >
                                <span className={`h-2.5 w-2.5 rounded-full ${
                                    isConnected ? 'bg-green-500' : 'bg-red-500'
                                }`} />
                            </span>
                            {selectedOrder && (
                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    aria-label="Bỏ chọn mã chung sức"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg text-xl font-semibold leading-none text-red-600 hover:bg-red-50"
                                    title="Bỏ chọn mã chung sức"
                                >
                                    ×
                                </button>
                            )}
                           
                        </div>
                    </div>
                </div>

                {activeNav === 'recharge' ? <DepositPage /> : activeNav === 'admin-recharge' ? <AdminDepositPage /> : activeNav === 'admin-pricing' ? <AdminOrderPricingPage /> : activeNav === 'admin-statistics' ? <AdminUserStatisticsPage /> : activeNav === 'admin-maintenance' ? <AdminMaintenancePage enabled={maintenanceEnabled} statusError={maintenanceStatusError} onRefresh={refreshMaintenanceStatus} onChange={(enabled) => { setMaintenanceEnabled(enabled); setMaintenanceStatusError(''); }} /> : activeNav === 'articles' ? <ArticlesPage isAdmin={isAdmin} /> : activeNav === 'orders' ? (
                    <OrderManagementPage
                        onSelectOrder={handleOpenOrderFromTable}
                        onToggleIntegrationPriority={handleToggleIntegrationPriority}
                        onCancelOrder={handleCancelOrder}
                        onUpdatePendingOrder={handleUpdatePendingOrder}
                        onRequestRedo={handleRequestOrderRedo}
                        onConfirmOrder={handleConfirmCompletedOrder}
                        onCancelOrderConfirmation={handleCancelOrderConfirmation}
                        onApproveRedo={handleApproveOrderRedo}
                        onRequestRefund={handleRequestOrderRefund}
                        onApproveRefund={handleApproveOrderRefund}
                        canPrioritizeOrders={isAdmin || isPartner}
                        canCancelOrders={canCancelOrders}
                        canEditPendingOrders={canCancelOrders}
                        canRequestRedo={user?.role === 'user' || user?.role === 'partner'}
                        canApproveRedo={isAdmin}
                        canRequestRefund={user?.role === 'user' || user?.role === 'partner'}
                        canApproveRefund={isAdmin}
                        orderCode={orderCode}
                        onOrderCodeChange={(value) => { setOrderCode(value); setOrderEntryError(''); setOrderEntryMessage(''); }}
                        orderSteps={orderSteps}
                        onOrderStepsChange={setOrderSteps}
                        orderPricing={orderPricing}
                        onCreateOrder={handleCreateOrder}
                        isCreating={isLoading}
                        createError={orderEntryError}
                        createMessage={orderEntryMessage}
                        maintenanceEnabled={maintenanceEnabled}
                        canCreateOrder
                    />
                ) : <>
                {/* Messages */}
                <div className="relative min-h-0 flex-1">
                <div
                    ref={messageListRef}
                    onScroll={handleMessageListScroll}
                    className="h-full overflow-y-auto p-4 space-y-3"
                >
                    {isLoadingOlderMessages && (
                        <div className="py-2 text-center text-xs text-gray-500">Đang tải tin nhắn cũ hơn...</div>
                    )}
                    {messages.map((message, messageIndex) => {
                        const isOwnMessage = String(message.userId) === String(currentUserId);
                        const replyTarget = typeof message.replyTo === 'string'
                            ? messages.find((item) => item._id === message.replyTo || item.id === message.replyTo)
                            : message.replyTo;
                        const replyTargetId = typeof message.replyTo === 'string'
                            ? message.replyTo
                            : message.replyTo?._id;
                        const replyTargetName = replyTarget && 'username' in replyTarget
                            ? replyTarget.username
                            : replyTarget?.senderFullName || replyTarget?.senderUsername;
                        const replyTargetContent = typeof message.replyTo !== 'string' && message.replyTo?.isDeleted
                            ? 'Tin nhắn đã bị xóa'
                            : replyTarget?.content || 'Nội dung không còn trong lịch sử';
                        const previousMessage = messages[messageIndex - 1];
                        const isNewDay = !previousMessage
                            || !isSameCalendarDay(previousMessage.timestamp, message.timestamp);
                        const statusLabel = {
                            sent: 'Đã gửi',
                            delivered: 'Đã nhận',
                            read: 'Đã đọc',
                        }[message.status || 'sent'];
                        const reactionSummary = MESSAGE_REACTIONS.map(({ emoji, label }) => {
                            const matchingReactions = (message.reactions || []).filter((reaction) => reaction.emoji === emoji);
                            return {
                                emoji,
                                label,
                                count: matchingReactions.length,
                                selected: matchingReactions.some((reaction) => reaction.userId === currentUserId),
                            };
                        }).filter((reaction) => reaction.count > 0);

                        return (
                            <React.Fragment key={message.id}>
                                {isNewDay && (
                                    <div className="flex items-center justify-center py-2 text-xs text-gray-500">
                                        <span className="rounded-full bg-gray-200 px-3 py-1 font-medium">
                                            {formatMessageDay(message.timestamp)}
                                        </span>
                                    </div>
                                )}
                                <div
                                data-order-id={message.type === 'order' ? message.orderId : undefined}
                                ref={(el) => {
                                    if (el) {
                                        messageRefs.current[message._id || message.id] = el;
                                    }
                                }}
                                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                                onClick={() => {
                                    // Admin can click on order messages to select and control order
                                    if (isAdmin && message.type === 'order' && message.orderId) {
                                        // First, try to find order from orders list
                                        let selectedOrd = orders.find(o => o._id === message.orderId);
                                        
                                        // If not found, create temporary order from message data
                                        if (!selectedOrd) {
                                            selectedOrd = {
                                                _id: message.orderId,
                                                code: message.content.match(/(?:Order|Đơn hàng) ([^:]+)/)?.[1] || 'Không xác định',
                                                userId: message.userId,
                                                username: message.username,
                                                status: (message.progress || 0) === 100 ? 'COMPLETED' : (message.progress || 0) > 0 ? 'PROCESSING' : 'PENDING',
                                                progress: message.progress || 0,
                                                currentStep: message.currentStep || 0,
                                                totalSteps: message.totalSteps || 8,
                                                createdAt: message.timestamp,
                                                updatedAt: message.timestamp,
                                            } as Order;
                                        }
                                        
                                        setSelectedOrder(selectedOrd);
                                        // Get latest order status from backend
                                        socketService.getOrderStatus(message.orderId);
                                    }
                                }}
                                >
                                <div className={`max-w-[70%] ${
                                    message.type === 'order'
                                        ? `bg-white border-2 border-blue-200 rounded-lg ${isAdmin ? 'cursor-pointer hover:shadow-lg hover:border-blue-400' : ''}`
                                        : isOwnMessage
                                            ? 'bg-blue-500 text-white rounded-l-xl rounded-tr-xl'
                                            : 'bg-white text-gray-800 rounded-r-xl rounded-tl-xl'
                                } p-3 shadow`}>
                                    {message.replyTo && (
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                scrollToMessage(replyTargetId);
                                            }}
                                            disabled={!replyTargetId}
                                            className={`mb-2 block w-full rounded-md border-l-2 px-2 py-1 text-left text-xs ${
                                                isOwnMessage
                                                    ? 'border-blue-200 bg-blue-600/50 text-blue-50'
                                                    : 'border-blue-400 bg-gray-50 text-gray-600'
                                            } disabled:cursor-default`}
                                            title="Tin nhắn được trả lời"
                                        >
                                            <span className="block font-semibold">
                                                {replyTargetName || 'Tin nhắn'}
                                            </span>
                                            <span className="block truncate">
                                                {replyTargetContent}
                                            </span>
                                        </button>
                                    )}
                                    {message.type === 'order' ? (
                                        // Order Progress Message
                                        <div className="text-gray-800">
                                            <div className="text-sm font-semibold mb-2 text-blue-600">{message.content}</div>
                                            {message.progress !== undefined && (
                                                <div>
                                                    {(() => {
                                                        const isOrderError = /(?:LỖI|ERROR)/i.test(message.content || '');
                                                        return (
                                                            <>
                                                    <div className="flex justify-between text-xs text-gray-600 mb-2">
                                                        <span>Tiến độ xử lý:</span>
                                                        <span className="font-semibold">{message.progress}% • Lần {message.currentStep}/{message.totalSteps}</span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                                        <div
                                                            className={`h-2.5 rounded-full transition-all duration-500 ${
                                                                isOrderError ? 'bg-red-500' : message.progress === 100 ? 'bg-green-500' :
                                                                message.progress >= 75 ? 'bg-blue-500' :
                                                                message.progress >= 50 ? 'bg-yellow-500' :
                                                                'bg-orange-500'
                                                            }`}
                                                            style={{ width: `${message.progress}%` }}
                                                        />
                                                    </div>
                                                    <div className="mt-2 text-xs text-gray-500">
                                                        {message.orderStatus && `Trạng thái: ${getOrderStatusLabel(message.orderStatus)} · `}
                                                        {isOrderError && '❌ Mã chung sức bị lỗi, cần tạo lại lần lỗi'}
                                                        {!isOrderError && message.progress === 0 && '⏳ Đang chờ xử lý...'}
                                                        {!isOrderError && message.progress > 0 && message.progress < 100 && '🔄 Đang xử lý...'}
                                                        {!isOrderError && message.progress === 100 && '✅ Hoàn thành!'}
                                                    </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                            {isAdmin && (
                                                <div className="mt-2 text-xs text-gray-500 italic">
                                                    💡 Nhấn để kiểm soát order
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="text-sm">{message.content}</div>
                                            {isOwnMessage && statusLabel && (
                                                <div className={`mt-1 flex justify-end text-[10px] ${
                                                    isOwnMessage ? 'text-blue-100' : 'text-gray-400'
                                                }`}>
                                                    <span>{statusLabel}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {message._id && message.type !== 'system' && (
                                        <div className={`mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] ${
                                            isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                                        }`} onClick={(event) => event.stopPropagation()}>
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setReplyingTo(message);
                                                    setMessageActionError('');
                                                }}
                                                className="hover:underline"
                                            >
                                                ↩ Trả lời
                                            </button>
                                            {isAdmin && !isOwnMessage && message.userId !== 'system' && (
                                                <button
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        void handleMarkMessageUnread(message);
                                                    }}
                                                    disabled={markingUnreadId === message._id}
                                                    className="hover:underline disabled:opacity-50"
                                                >
                                                    {markingUnreadId === message._id ? 'Đang cập nhật...' : 'Đánh dấu chưa đọc'}
                                                </button>
                                            )}
                                            {isAdmin
                                                && !isOwnMessage
                                                && message.orderId
                                                && message.content.startsWith('🔁 Yêu cầu làm lại đơn hàng ')
                                                && !approvedRedoMessageIds.includes(message._id)
                                                && (
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            void handleApproveRedoFromMessage(message);
                                                        }}
                                                        disabled={approvingRedoMessageId === message._id}
                                                        className="font-semibold text-green-700 hover:underline disabled:opacity-50"
                                                    >
                                                        {approvingRedoMessageId === message._id ? 'Đang duyệt...' : 'Duyệt làm lại miễn phí'}
                                                    </button>
                                                )}
                                            {isAdmin
                                                && !isOwnMessage
                                                && message.orderId
                                                && message.content.startsWith('💸 Yêu cầu hoàn tiền đơn hàng ')
                                                && message._id
                                                && !approvedRefundMessageIds.includes(message._id)
                                                && (
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            void handleApproveRefundFromMessage(message);
                                                        }}
                                                        disabled={approvingRefundMessageId === message._id}
                                                        className="font-semibold text-green-700 hover:underline disabled:opacity-50"
                                                    >
                                                        {approvingRefundMessageId === message._id ? 'Đang duyệt...' : 'Duyệt hoàn tiền'}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                                                {reactionSummary.map((reaction) => (
                                                    <button
                                                        key={reaction.emoji}
                                                        type="button"
                                                        onClick={() => void handleToggleMessageReaction(message, reaction.emoji)}
                                                        disabled={reactingMessageId === message._id}
                                                        aria-label={`${reaction.label}, ${reaction.count} cảm xúc${reaction.selected ? ', bạn đã chọn' : ''}`}
                                                        aria-pressed={reaction.selected}
                                                        title={reaction.label}
                                                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition disabled:opacity-50 ${
                                                            reaction.selected
                                                                ? 'border-blue-400 bg-blue-50 text-blue-700'
                                                                : 'border-gray-200 bg-white/90 text-gray-700 hover:border-blue-300'
                                                        }`}
                                                    >
                                                        <span>{reaction.emoji}</span>
                                                        <span>{reaction.count}</span>
                                                    </button>
                                                ))}
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setActiveReactionMessageId((currentId) => currentId === message._id ? null : message._id || null)}
                                                        disabled={reactingMessageId === message._id}
                                                        aria-label="Thêm cảm xúc"
                                                        aria-expanded={activeReactionMessageId === message._id}
                                                        title="Thêm cảm xúc"
                                                        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-sm text-gray-600 transition hover:border-blue-300 hover:text-blue-600 disabled:opacity-50"
                                                    >
                                                        ☺+
                                                    </button>
                                                    {activeReactionMessageId === message._id && (
                                                        <div className="absolute bottom-full right-0 z-30 mb-2 flex gap-1 rounded-full border border-gray-200 bg-white p-1.5 shadow-lg">
                                                            {MESSAGE_REACTIONS.map((reaction) => (
                                                                <button
                                                                    key={reaction.emoji}
                                                                    type="button"
                                                                    onClick={() => void handleToggleMessageReaction(message, reaction.emoji)}
                                                                    disabled={reactingMessageId === message._id}
                                                                    aria-label={reaction.label}
                                                                    title={reaction.label}
                                                                    className="flex h-9 w-9 items-center justify-center rounded-full text-xl transition hover:scale-125 hover:bg-gray-100 disabled:opacity-50"
                                                                >
                                                                    {reaction.emoji}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className={`mt-2 text-[10px] ${
                                        isOwnMessage ? 'text-blue-100' : 'text-gray-400'
                                    }`}>
                                        {formatMessageTime(message.timestamp)}
                                    </div>
                                </div>
                                </div>
                            </React.Fragment>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>
                {showScrollToLatest && (
                    <button
                        type="button"
                        onClick={scrollToLatestMessage}
                        aria-label="Cuộn xuống tin nhắn mới nhất"
                        title="Tin nhắn mới nhất"
                        className="absolute bottom-4 right-5 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-lg transition hover:bg-gray-50"
                    >
                        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                            <path d="M12 4v15" />
                            <path d="m6 13 6 6 6-6" />
                        </svg>
                    </button>
                )}
                </div>

                {/* Input */}
                <div className="sticky bottom-0 z-10 w-full shrink-0 border-t border-gray-200 bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
                    {replyingTo && (
                        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border-l-4 border-blue-500 bg-blue-50 px-3 py-2">
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-blue-700">
                                    Đang trả lời {replyingTo.username}
                                </p>
                                <p className="truncate text-sm text-gray-600">{replyingTo.content}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setReplyingTo(null)}
                                className="shrink-0 rounded px-2 py-1 text-gray-500 hover:bg-blue-100"
                                aria-label="Hủy trả lời"
                            >
                                ×
                            </button>
                        </div>
                    )}
                    {messageActionError && (
                        <p role="alert" className="mb-2 text-sm text-red-600">{messageActionError}</p>
                    )}
                    <div className="flex min-w-0 w-full gap-2">
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    const reader = new FileReader();
                                    reader.onload = (event) => {
                                        addMessage({
                                            id: `msg-${Date.now()}`,
                                            userId,
                                            username,
                                            content: `📷 Đã tải ảnh lên`,
                                            type: 'image',
                                            imageUrl: event.target?.result as string,
                                            timestamp: new Date(),
                                            isAdmin: true,
                                        });
                                    };
                                    reader.readAsDataURL(file);
                                }
                                e.target.value = '';
                            }}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="shrink-0 rounded-lg p-2 hover:bg-gray-100"
                            title="Đính kèm hình ảnh"
                        >
                            🖼️
                        </button>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="shrink-0 rounded-lg p-2 hover:bg-gray-100"
                            title="Đính kèm tệp"
                        >
                            📎
                        </button>
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                            placeholder="Nhập tin nhắn..."
                            className="min-w-0 flex-1 rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={handleSendMessage}
                            className="shrink-0 rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                        >
                            📤
                        </button>
                    </div>
                </div>
                </>}
            </div>

            {isChangePasswordOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !isChangingPassword && setIsChangePasswordOpen(false)}>
                    <form
                        onSubmit={handleChangePassword}
                        onClick={(event) => event.stopPropagation()}
                        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Đổi mật khẩu</h2>
                                <p className="mt-1 text-sm text-gray-500">Nhập mật khẩu hiện tại và mật khẩu mới.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsChangePasswordOpen(false)}
                                disabled={isChangingPassword}
                                className="text-xl text-gray-500 disabled:opacity-50"
                                aria-label="Đóng"
                            >
                                ×
                            </button>
                        </div>
                        <div className="mt-5 space-y-3">
                            <input
                                type="password"
                                value={passwordForm.currentPassword}
                                onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })}
                                placeholder="Mật khẩu hiện tại"
                                required
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                            />
                            <input
                                type="password"
                                value={passwordForm.newPassword}
                                onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })}
                                placeholder="Mật khẩu mới"
                                minLength={6}
                                required
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                            />
                            <input
                                type="password"
                                value={passwordForm.confirmPassword}
                                onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })}
                                placeholder="Nhập lại mật khẩu mới"
                                minLength={6}
                                required
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-blue-500"
                            />
                        </div>
                        {(passwordError || passwordMessage) && (
                            <p className={`mt-3 text-sm ${passwordError ? 'text-red-600' : 'text-green-600'}`}>
                                {passwordError || passwordMessage}
                            </p>
                        )}
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setIsChangePasswordOpen(false)}
                                disabled={isChangingPassword}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Hủy
                            </button>
                            <button
                                type="submit"
                                disabled={isChangingPassword}
                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isChangingPassword ? 'Đang lưu...' : 'Đổi mật khẩu'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ChatInterface;